import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { T, TF } from "react-translator-component";
import { Box, TextField, Typography } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import dayjs, { Dayjs } from "dayjs";
import GCBackButton from "../components/GreenCity/GCBackButton";
import SubmitButton from "../components/SubmitButton";
import LoadingModal from "../components/LoadingModal";
import { ErrorMessageModal } from "../components/ErrorMessageModal";
import DateSelectDialog from "../components/GreenCity/DateSelectDialog";
import { styles as oldstyles } from "./styles";
import profile from "../assets/images/profile_img.png";
import appServices from "../services/app-services/appServices";
import { convertUserImage } from "../util/utils";

const CreateLeaderEventPost = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { personalPlan } = location.state || {};
  const { user } = useSelector((state: any) => state.userAuth);
  const [loading, setLoading] = useState<boolean>(false);
  const [openMessageModal, setOpenMessageModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string[]>([])
  const [title, setTitle] = useState<string>("");
  const [eventOverview, setEventOverview] = useState<string>("");
  const [websiteURL, setWebsiteURL] = useState<string>("");
  const [eventStartDate, setEventStartDate] = useState<Dayjs | null>(
    dayjs(new Date())
  );
  const [eventEndDate, setEventEndDate] = useState<Dayjs | null>(
    eventStartDate
  );
  const [errors, setErrors] = useState<any>({
    title: "",
    eventStartDate: "",
    eventEndDate: ""
  });

  const minStartDate = dayjs()

  const validateForm = () => {
    let valid = true;

    let newErrors = {
      title: "",
      eventStartDate: "",
      eventEndDate: ""
    };
    if (title === "") {
      newErrors.title = TF("{0}{1}", T("title"), T("error_enter"))
      valid = false;
    }
    if (eventStartDate === null) {
      newErrors.eventStartDate = TF("{0}{1}", T("start_date"), T("error_enter"))
      valid = false
    }
    if (eventEndDate === null) {
      newErrors.eventEndDate = TF("{0}{1}", T("end_date"), T("error_enter"))
      valid = false
    }
    if (eventStartDate && eventEndDate && eventEndDate.isBefore(eventStartDate)) {
      newErrors.eventEndDate = TF("personal_plan_error_date_after", T("end_date"), T("start_date"))
      valid = false
    }
    setErrors(newErrors);
    return valid;
  };
  const submit = async () => {
    setLoading(true);

    if (!validateForm()) {
      setLoading(false);
      window.scrollTo(0, 0);
      return false;
    }

    try {
      const body = {
        id: personalPlan?.id ? personalPlan?.id : "",
        user_id: user?.id,
        title: title,
        start_date: eventStartDate?.format("YYYY-M-D"),
        end_date: eventEndDate?.format("YYYY-M-D"),
        description: eventOverview,
        web_url: websiteURL,
      };

      if (personalPlan) {
        const res = await appServices.editPersonalPlan(personalPlan?.id, body);
      } else {
        const res = await appServices.createPersonalPlan(body);
      }

      setLoading(false);
      navigate("/myfarm");
      return true
    } catch (error: any) {
      setLoading(false);
      console.error(error);
      setErrorMessage(error?.response?.data?.message ?? [T("api_unknown_error")])
      setOpenMessageModal(true)
      return false
    }
  };

  const closeMessageModal = () => {
    setOpenMessageModal(false)
    setErrorMessage([])
  }

  useEffect(() => {
    if (personalPlan) {
      setTitle(personalPlan?.title);
      setEventStartDate(dayjs(personalPlan?.start_date));
      setEventEndDate(dayjs(personalPlan?.end_date));
      setEventOverview(personalPlan?.description);
      setWebsiteURL(personalPlan?.web_url);
    }
  }, [personalPlan]);

  useEffect(() => {
    if (!eventStartDate) return
    dayjs(eventStartDate)?.isAfter(eventEndDate) && setEventEndDate(dayjs(eventStartDate))
  }, [eventStartDate])

  return (
    <div>
      <div
        style={{
          paddingTop: 90,
          paddingLeft: 16,
          paddingBottom: "12px",
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.3)",
        }}
      >
        <LoadingModal visible={loading} handleClose={() => {}} />

        <GCBackButton onClick={() => navigate(-1)} />
        <Box sx={{ marginTop: 1, display: "flex", gap: 1 }}>
          <img
            src={
              user.image !== null
                ? convertUserImage(user.image)
                : profile
            }
            alt={"userimg"}
            style={styles.userIcon}
          />

          <span style={{ ...oldstyles.farmInfoTitle, color: "black" }}>
            {user.nickname}
          </span>
        </Box>
      </div>
      <div style={{ padding: "0px 15px 20px 15px" }}>
        <Typography sx={styles.typographyText}>{TF("{0}{1}", T("personal_plan_name"), T("required"))}</Typography>
        <TextField
          style={styles.textField}
          onChange={(event) => {
            if (event.target.value.length > 30) return;
            setTitle(event.target.value);
            setErrors({ ...errors, title: "" });
          }}
          value={title}
          multiline
          rows={1} // Set the number of rows
          variant="outlined" // You can choose "filled" or "standard" as well
          fullWidth // Makes the TextField take the full width of its container
          error={!!errors.title}
          helperText={errors.title}
        />

        <Typography sx={styles.typographyText}>{T("personal_plan_date")}</Typography>

        <Typography
          sx={{
            paddingTop: "30px",
            textAlign: "left",
            fontSize: "15px",
          }}
        >
          {TF("{0}{1}", T("start_date"), T("required"))}
        </Typography>
        <DateSelectDialog
          inputedDate={eventStartDate}
          minDate={minStartDate}
          errorMessage={errors.eventStartDate}
          onChange={(date)=>{setEventStartDate(date)}}
        />

        <Typography
          sx={{
            paddingTop: "30px",
            textAlign: "left",
            fontSize: "15px",
          }}
        >
          {TF("{0}{1}", T("end_date"), T("end_date_description"))}
        </Typography>
        <DateSelectDialog
          inputedDate={eventEndDate}
          minDate={eventStartDate}
          errorMessage={errors.eventEndDate}
          onChange={(date)=>{setEventEndDate(date)}}
        />

        <Typography sx={styles.typographyText}>{TF("{0}{1}", T("personal_plan_description"), T("optional"))}</Typography>
        <TextField
          style={styles.textField}
          onChange={(event) => setEventOverview(event.target.value)}
          value={eventOverview}
          multiline
          rows={3} // Set the number of rows
          variant="outlined" // You can choose "filled" or "standard" as well
          fullWidth // Makes the TextField take the full width of its container
        />

        <Typography sx={styles.typographyText}>{TF("{0}{1}", T("url"), T("optional"))}</Typography>
        <TextField
          style={styles.textField}
          onChange={(event) => setWebsiteURL(event.target.value)}
          value={websiteURL}
          placeholder={`${T("url_placeholder")}`}
          multiline
          rows={2} // Set the number of rows
          variant="outlined" // You can choose "filled" or "standard" as well
          fullWidth // Makes the TextField take the full width of its container
        />
        <SubmitButton
          color="secondary"
          sx={styles.submitButton}
          size={"large"}
          variant="contained"
          validate={async () => true}
          onSubmit={submit}
          endIcon={
            <ArrowForwardIosIcon
              sx={{ position: "absolute", right: 0, top: 0, margin: "10px" }}
            />
          }
        >
          {T("personal_plan_submit")}
        </SubmitButton>
      </div>
      <ErrorMessageModal
        open={openMessageModal}
        messages={errorMessage}
        onClose={() => closeMessageModal()}
      />
    </div>
  );
};

const styles = {
  eventPostingBox: {
    display: "flex",
    gap: "15px",
    alignItmes: "center",
    paddingTop: "10px",
    paddingLeft: "10px",
  },
  textField: {
    marginTop: "15px",
    background: "white",
  },
  userIcon: {
    width: "30px",
    height: "30px",
    borderRadius: "35px",
  },
  typographyText: {
    borderBottom: "2px solid #86BB57",
    paddingTop: "50px",
    textAlign: "left",
    fontSize: "15px",
  },
  submitButton: {
    width: "80%",
    alignSelf: "center",
    margin: "0 auto",
    marginTop: "20px",
    display: "flex",
    borderRadius: 20,
    border: "2px solid",
  },
};
export default CreateLeaderEventPost;
